import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft, ArrowRight, GitMerge, CheckCircle, AlertTriangle,
    Loader2, Users, ExternalLink
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import { ErrorAlert } from '../shared/ErrorAlert';
import ConfirmMergeModal from './ConfirmMergeModal';

const STEPS = [
    { label: 'Select Primary', description: 'Choose the primary client record' },
    { label: 'Preview Conflicts', description: 'Review field conflicts and reassignments' },
    { label: 'Review & Confirm', description: 'Final review before merge' },
    { label: 'Result', description: 'Merge complete' },
];

const MergeWorkflow = () => {
    const { primaryId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState(0);
    const [clients, setClients] = useState([]);
    const [selectedPrimaryId, setSelectedPrimaryId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [fieldOverrides, setFieldOverrides] = useState({});
    const [reason, setReason] = useState('');
    const [mergeResult, setMergeResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [merging, setMerging] = useState(false);
    const [error, setError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Load duplicate group from navigate state or fetch from API
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const duplicateGroup = location.state?.duplicateGroup;

                if (duplicateGroup && duplicateGroup.clients?.length > 0) {
                    setClients(duplicateGroup.clients);
                    setSelectedPrimaryId(duplicateGroup.clients[0].id);
                } else {
                    // Fallback: fetch duplicates for this client
                    const response = await dataQualityService.duplicates.getForClient(primaryId);
                    if (response.success && response.data && response.data.length > 0) {
                        const group = response.data[0];
                        setClients(group.clients || []);
                        setSelectedPrimaryId(Number(primaryId));
                    } else {
                        setError('No duplicate group found for this client');
                    }
                }
            } catch (err) {
                console.error('Error loading merge data:', err);
                setError('Failed to load merge data');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [primaryId, location.state]);

    const sourceClientIds = clients
        .filter(c => c.id !== selectedPrimaryId)
        .map(c => c.id);

    const handlePreview = async () => {
        try {
            setPreviewLoading(true);
            setError('');

            const response = await dataQualityService.merge.preview({
                primaryClientId: selectedPrimaryId,
                sourceClientIds,
                fieldOverrides: {},
                confirmed: false,
            });

            if (response.success && response.data) {
                setPreview(response.data);
                // Initialize field overrides with recommended values
                const initialOverrides = {};
                (response.data.fieldConflicts || []).forEach(conflict => {
                    if (conflict.recommendedClientId) {
                        initialOverrides[conflict.fieldName] = conflict.recommendedClientId;
                    }
                });
                setFieldOverrides(initialOverrides);
                setStep(1);
            } else {
                setError(response.message || 'Failed to generate merge preview');
            }
        } catch (err) {
            console.error('Error previewing merge:', err);
            setError(err.message || 'Failed to preview merge');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleExecuteMerge = async () => {
        try {
            setMerging(true);
            setError('');
            setShowConfirmModal(false);

            const response = await dataQualityService.merge.execute({
                primaryClientId: selectedPrimaryId,
                sourceClientIds,
                fieldOverrides,
                reason,
                confirmed: true,
            });

            if (response.success && response.data) {
                setMergeResult(response.data);
                setStep(3);
            } else {
                setError(response.message || 'Merge execution failed');
            }
        } catch (err) {
            console.error('Error executing merge:', err);
            setError(err.message || 'Merge execution failed');
        } finally {
            setMerging(false);
        }
    };

    const primaryClient = clients.find(c => c.id === selectedPrimaryId);
    const sourceClients = clients.filter(c => c.id !== selectedPrimaryId);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/data-quality/duplicates')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">Merge Workflow</h2>
                        <p className="text-sm text-gray-500">{clients.length} clients in this group</p>
                    </div>
                </div>
            </div>

            {/* Step Indicator */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                    {STEPS.map((s, idx) => (
                        <React.Fragment key={idx}>
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                    idx < step ? 'bg-green-500 text-white' :
                                    idx === step ? 'bg-blue-600 text-white' :
                                    'bg-gray-200 text-gray-500'
                                }`}>
                                    {idx < step ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                                </div>
                                <div className="hidden md:block">
                                    <p className={`text-sm font-medium ${idx === step ? 'text-blue-600' : 'text-gray-500'}`}>
                                        {s.label}
                                    </p>
                                </div>
                            </div>
                            {idx < STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-2 ${idx < step ? 'bg-green-500' : 'bg-gray-200'}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {error && <ErrorAlert error={error} />}

            {/* Step 0: Select Primary */}
            {step === 0 && (
                <div className="space-y-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Primary Client</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            The primary client record will be kept. All data from other clients will be merged into it.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {clients.map((client) => (
                                <label
                                    key={client.id}
                                    className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                                        selectedPrimaryId === client.id
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-blue-300'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="radio"
                                            name="primaryClient"
                                            checked={selectedPrimaryId === client.id}
                                            onChange={() => setSelectedPrimaryId(client.id)}
                                            className="mt-1"
                                        />
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900">{client.name}</p>
                                            <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                                                <p>Code: {client.clientCode}</p>
                                                <p>Type: {client.clientType}</p>
                                                {client.email && <p>Email: {client.email}</p>}
                                                {client.phoneNumber && <p>Phone: {client.phoneNumber}</p>}
                                                {client.nationalId && <p>National ID: {client.nationalId}</p>}
                                                <p>Products: {client.productCount} | KYC: {client.kycDocumentCount}</p>
                                            </div>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handlePreview}
                            disabled={!selectedPrimaryId || previewLoading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {previewLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Generating Preview...
                                </>
                            ) : (
                                <>
                                    Next: Preview
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 1: Preview Conflicts */}
            {step === 1 && preview && (
                <div className="space-y-4">
                    {/* Warnings */}
                    {preview.warnings?.length > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                <p className="font-medium text-yellow-800">Warnings</p>
                            </div>
                            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                                {preview.warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                            </ul>
                        </div>
                    )}

                    {/* Reassignment Preview */}
                    {preview.reassignmentPreview && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Reassignment Summary</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <p className="text-sm text-blue-600">Products</p>
                                    <p className="text-xl font-bold text-blue-900">{preview.reassignmentPreview.products}</p>
                                    {preview.reassignmentPreview.duplicateProducts > 0 && (
                                        <p className="text-xs text-blue-500">{preview.reassignmentPreview.duplicateProducts} duplicates</p>
                                    )}
                                </div>
                                <div className="bg-green-50 rounded-lg p-3">
                                    <p className="text-sm text-green-600">KYC Documents</p>
                                    <p className="text-xl font-bold text-green-900">{preview.reassignmentPreview.kycDocuments}</p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3">
                                    <p className="text-sm text-purple-600">Assets</p>
                                    <p className="text-xl font-bold text-purple-900">{preview.reassignmentPreview.assets}</p>
                                </div>
                                <div className="bg-orange-50 rounded-lg p-3">
                                    <p className="text-sm text-orange-600">Dependants</p>
                                    <p className="text-xl font-bold text-orange-900">{preview.reassignmentPreview.dependants}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Field Conflicts */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Field Conflicts</h3>
                            <p className="text-sm text-gray-500">Select which value to keep for each conflicting field</p>
                        </div>

                        {(!preview.fieldConflicts || preview.fieldConflicts.length === 0) ? (
                            <div className="p-6 text-center text-gray-500">
                                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                <p>No field conflicts found</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                        {clients.map(c => (
                                            <th key={c.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {c.name}
                                                {c.id === selectedPrimaryId && (
                                                    <span className="ml-1 text-blue-600">(Primary)</span>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {preview.fieldConflicts.map((conflict) => (
                                        <tr key={conflict.fieldName} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <p className="text-sm font-medium text-gray-900">{conflict.fieldLabel}</p>
                                                <p className="text-xs text-gray-400">{conflict.fieldName}</p>
                                            </td>
                                            {clients.map(c => {
                                                const value = conflict.valuesByClientId?.[c.id] || '-';
                                                const isSelected = fieldOverrides[conflict.fieldName] === c.id;
                                                return (
                                                    <td key={c.id} className="px-6 py-4 whitespace-nowrap">
                                                        <label className={`flex items-center gap-2 p-2 rounded cursor-pointer transition ${
                                                            isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                                                        }`}>
                                                            <input
                                                                type="radio"
                                                                name={`conflict-${conflict.fieldName}`}
                                                                checked={isSelected}
                                                                onChange={() => setFieldOverrides(prev => ({
                                                                    ...prev,
                                                                    [conflict.fieldName]: c.id
                                                                }))}
                                                            />
                                                            <span className="text-sm text-gray-700">{value}</span>
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={() => setStep(0)}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Back
                        </button>
                        <button
                            onClick={() => setStep(2)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                        >
                            Next: Review
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Step 2: Review & Confirm */}
            {step === 2 && (
                <div className="space-y-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Merge Summary</h3>

                        <div className="space-y-4">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Primary Client (keeping)</p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {primaryClient?.name} ({primaryClient?.clientCode})
                                </p>
                            </div>

                            <div>
                                <p className="text-sm font-medium text-gray-500">Merging Into Primary</p>
                                <div className="space-y-1 mt-1">
                                    {sourceClients.map(c => (
                                        <p key={c.id} className="text-sm text-gray-700">
                                            {c.name} ({c.clientCode}) - will be deleted
                                        </p>
                                    ))}
                                </div>
                            </div>

                            {Object.keys(fieldOverrides).length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Field Overrides</p>
                                    <div className="mt-1 space-y-1">
                                        {Object.entries(fieldOverrides).map(([field, clientId]) => {
                                            const client = clients.find(c => c.id === clientId);
                                            return (
                                                <p key={field} className="text-sm text-gray-700">
                                                    {field}: using value from {client?.name || `Client #${clientId}`}
                                                </p>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for Merge <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Explain why these clients are being merged..."
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={() => setStep(1)}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Back
                        </button>
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            disabled={!reason.trim() || merging}
                            className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {merging ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Merging...
                                </>
                            ) : (
                                <>
                                    <GitMerge className="w-4 h-4" />
                                    Execute Merge
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 3: Result */}
            {step === 3 && mergeResult && (
                <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                        <h3 className="text-xl font-semibold text-green-800 mb-2">Merge Successful</h3>
                        <p className="text-green-700">
                            Clients have been successfully merged into{' '}
                            <strong>{mergeResult.mergedClient?.firstName} {mergeResult.mergedClient?.lastName || mergeResult.mergedClient?.companyName}</strong>
                        </p>
                    </div>

                    {/* Reassignment Summary */}
                    {mergeResult.reassignmentSummary && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Reassignment Summary</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.entries(mergeResult.reassignmentSummary).map(([key, count]) => (
                                    <div key={key} className="bg-gray-50 rounded-lg p-3">
                                        <p className="text-sm text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                                        <p className="text-xl font-bold text-gray-900">{count}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {mergeResult.deletedClientCodes?.length > 0 && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <p className="text-sm text-gray-500">Deleted Client Codes</p>
                            <div className="flex gap-2 mt-2 flex-wrap">
                                {mergeResult.deletedClientCodes.map((code) => (
                                    <span key={code} className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">{code}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 justify-center">
                        {mergeResult.mergedClient?.id && (
                            <button
                                onClick={() => navigate(`/clients/${mergeResult.mergedClient.id}`)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                View Merged Client
                            </button>
                        )}
                        <button
                            onClick={() => navigate('/data-quality/duplicates')}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Back to Duplicates
                        </button>
                    </div>
                </div>
            )}

            {/* Confirm Merge Modal */}
            <ConfirmMergeModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleExecuteMerge}
                primaryName={primaryClient?.name}
                sourceNames={sourceClients.map(c => c.name)}
                loading={merging}
            />
        </div>
    );
};

export default MergeWorkflow;
